import { ChartTooltip } from '@/components/analytics/ChartTooltip.tsx'
import type { ComposedChartConfig } from '@/components/analytics/ComposedLineBarConfig.tsx'
import {
  CHART_BAR_COLOR,
  CHART_CURSOR_STROKE_COLOR,
  CHART_DOT_ACTIVE_COLOR,
  CHART_GRID_BORDER_COLOR,
  CHART_X_AXIS_TICK_LABEL_COLOR,
  CHART_Y_AXIS_TICK_LABEL_COLOR,
  LINE_CHART_STROKE_COLOR,
} from '@/constants/ANALYTICS.ts'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import './chart-styles.css'
import { useState } from 'react'

interface ComposedChartProps<T extends object> {
  data: T[]
  config: ComposedChartConfig<T>
  height: number
  showCursorLine?: boolean
}

export function ComposedLineBarChart<T extends object>({
  data,
  config,
  height,
  showCursorLine = true,
}: ComposedChartProps<T>) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const chartData = data.map((item, index, array) => ({
    ...item,
    xValue: config.xFormatter(item, index, array),
  }))

  // biome-ignore lint/suspicious/noExplicitAny: Properties required by recharts
  const renderDot = (props: any, active: boolean) => {
    const { cx, cy, index } = props
    const activeRadius = 3.5
    const inactiveRadius = 2.5
    return (
      <circle
        key={index}
        cx={cx}
        cy={cy}
        r={active ? activeRadius : inactiveRadius}
        stroke={index === chartData.length - 1 ? CHART_DOT_ACTIVE_COLOR : LINE_CHART_STROKE_COLOR}
        strokeWidth={1}
        fill={index === chartData.length - 1 ? CHART_DOT_ACTIVE_COLOR : LINE_CHART_STROKE_COLOR}
      />
    )
  }

  // biome-ignore lint/suspicious/noExplicitAny: Properties required by recharts
  function CustomCursor(props: any) {
    const { pointerEvents, height, points, className } = props

    const { x, y } = points[0]

    const width = 0.5

    return (
      <Rectangle
        x={x - width / 2}
        y={y}
        fillOpacity={0}
        stroke={CHART_CURSOR_STROKE_COLOR}
        pointerEvents={pointerEvents}
        width={width}
        height={height}
        points={points}
        className={className}
      />
    )
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        className="grid-last-line-only"
        data={chartData}
        margin={{ top: 8, right: 10, bottom: 0, left: 10 }}
        onMouseMove={(state) => {
          if (state && state.activeTooltipIndex !== undefined) {
            setActiveIndex(state.activeTooltipIndex)
          }
        }}
        onMouseLeave={() => setActiveIndex(null)}
      >
        {!config.hideGrid && <CartesianGrid horizontal={true} vertical={true} stroke={CHART_GRID_BORDER_COLOR} />}

        <XAxis
          dataKey="xValue"
          hide={config.hideXAxis}
          tickLine={false}
          axisLine={{ stroke: CHART_GRID_BORDER_COLOR }}
          tick={{ fill: CHART_X_AXIS_TICK_LABEL_COLOR, fontSize: 12 }}
          tickFormatter={(value, index) => {
            return index === data.length - 1 ? 'Current' : value
          }}
          interval={0}
        />

        <YAxis
          stroke=""
          hide={config.hideYAxis}
          width={20}
          axisLine={{ stroke: CHART_GRID_BORDER_COLOR }}
          tick={{ fill: CHART_Y_AXIS_TICK_LABEL_COLOR, fontSize: 10 }}
          tickFormatter={(value, index) => {
            return index === 0 || index === data.length - 1 ? value : ''
          }}
        />

        <Line
          hide={config.hideLine || !showCursorLine}
          dataKey={String(config.lineKey)}
          stroke={LINE_CHART_STROKE_COLOR}
          strokeWidth={1}
          dot={(props) => renderDot(props, false)}
          // biome-ignore lint/suspicious/noExplicitAny: Properties required by recharts
          activeDot={(props: any) => renderDot(props, true)}
          isAnimationActive={false}
          connectNulls={false}
        />

        <Bar
          hide={config.hideBar}
          fill={CHART_BAR_COLOR}
          dataKey={String(config.barKey)}
          barSize={36}
          minPointSize={4}
          isAnimationActive={false}
          // biome-ignore lint/suspicious/noExplicitAny: Properties required by recharts
          shape={(props: any) => {
            const { x, y, width, height, index } = props
            const isActive = index === activeIndex
            return (
              <Rectangle
                x={x}
                y={y}
                width={width}
                height={height}
                fill={isActive ? CHART_CURSOR_STROKE_COLOR : CHART_BAR_COLOR}
                radius={[4, 4, 0, 0]}
              />
            )
          }}
        />

        <Tooltip
          content={config.tooltip && <ChartTooltip<T> {...config.tooltip} />}
          cursor={showCursorLine ? <CustomCursor line={config.hideBar} /> : false}
          position={{ y: height - 10 }}
          wrapperStyle={{ zIndex: 1000 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
